import {
  find,
  cloneDeep,
  isArray
} from 'lodash';
import moment from 'moment';


/**
 * 为 tree 的每一个 item 添加 key，由 id 生成；改变原数组
 * @param child 待处理的 tree
 * @param level key的起始命名
 * @param parent 过程变量，存放结果
 * @param key 添加的字段名称，默认为 'key'
 * @param connector 连接符，默认为 '-'
 * @param relationName 父子之间的关系字段，默认为 'children'
 * @returns {Array}作为入参的 tree 本身。
 */
export function makeTreeItemKeyed(child, id = 'task_id', level = 0, parent = null, key = 'key', connector = '-', relationName = 'children') {
  function getKeyedTree(child, level = 0, parent = null, key = 'key', connector = '-', relationName = 'children') {
    const result = child.map(value => {
      value[key] = `${level}${connector}${value[id]}`;
      if (value[relationName]) {
        getKeyedTree(value[relationName], value[key], value);
      }
      return value;
    });

    if (parent) {
      parent[relationName] = result
    } else {
      parent = result;
    }

    return parent;
  }

  child.splice(0, child.length, ...getKeyedTree(child, level, parent, key, connector, relationName));

  return child;
}

export function makeTreeItemHasSomePropBy(child, by = 'task_name', parent = null, key = 'title', relationName = 'children') {
  function getKeyedTree(child, by = 'task_name', parent = null, key = 'title', relationName = 'children') {
    const result = child.map(value => {
      value[key] = value[by];
      if (value[relationName]) {
        getKeyedTree(value[relationName], by, value, 'title');
      }
      return value;
    });

    if (parent) {
      parent[relationName] = result
    } else {
      parent = result;
    }

    return parent;
  }

  child.splice(0, child.length, ...getKeyedTree(child, by, parent, key, relationName));

  return child;
}

export function makeTreeItemHasSomeProp(child, key = 'disableCheckBox', parent = null, val = null, relationName = 'children') {
  function getKeyedTree(child, key = 'disableCheckBox', parent = null, val = null, relationName = 'children') {
    const result = child.map(value => {
      value[key] = val;
      if (value[relationName]) {
        getKeyedTree(value[relationName], 'disableCheckBox', value, val);
      }
      return value;
    });

    if (parent) {
      parent[relationName] = result
    } else {
      parent = result;
    }

    return parent;
  }

  child.splice(0, child.length, ...getKeyedTree(child, key, parent, val, relationName));

  return child;
}

/**
 * 根据 key 生成 group，group: key 的父级、爷爷、祖先...。id 中不允许包含 connector
 * @param keys 处于选中状态的 key 组成的数组；
 * @param connector 连接符，默认为 '-'
 * @returns {Array}改变原数组，返回 group 在一起的数组。
 */
export function generateGroupByKey(keys, connector = '-') {
  if (!isArray(keys)) {
    console.log('generateGroup 的参数必须是数组');
    return [];
  }

  const result = [];
  keys.forEach(value => {
    result.push(value);
    let key = value;
    while (key.split(connector).length > 2) {
      key = key.slice(0, key.lastIndexOf(connector));
      if (!keys.includes(key)) {
        result.push(key);
      }
    }
  });
  keys.splice(0, keys.length, ...result);

  return keys;
}

/**
 * 树状数据结构打平。relationName 字段默认为 children 。不改变原数组，返回新数组
 * @param tree 树状结构数据
 * @param flat 过程变量，存放结果
 * @param relationName relationName 父子之间的关系字段，默认为 'children'
 * @returns {Array}不改变原数据，返回新数组，平铺的数据结构
 */
export function treeToFlat(tree, flat = [], relationName = 'children') {
  const treeCopy = cloneDeep(tree);

  treeCopy.forEach(value => {
    const valueCopy = cloneDeep(value);
    delete valueCopy[relationName];
    flat.push(valueCopy);

    if (value[relationName]) {
      treeToFlat(value[relationName], flat);
    }
  });

  return flat;

}

/**
 * 根据打平的数据生成树状数据。不改变原数组，返回新数组。relationName 默认为 children
 * @param flatData 入参，平铺结构的数据
 * @param relationName relationName relationName 父子之间的关系字段，默认为 'children'
 * @returns {Array} 不改变原数组，返回新的具有树状结构的数组
 */
export function flatToTree(flatData, relationName = 'children') {
  let flatDataCopy = cloneDeep(flatData);
  let sameClass = [];
  let level = 1;

  while (flatDataCopy.length > 0) {
    let deleteItems = [];

    flatDataCopy.forEach((value) => {
      if (value.key.split('-').length - 1 === level) {
        deleteItems.push(value);
      }
    });

    sameClass.push(deleteItems);

    level++;
    // 抛出掉
    flatDataCopy = flatDataCopy.filter(({
      key
    }) => {
      return !find(deleteItems, {
        key
      });
    });
  }

  for (let i = sameClass.length - 1; i > 0; i--) {
    for (let value of sameClass[i]) {
      for (let p of sameClass[i - 1]) {
        if (p.key === value.key.slice(0, value.key.lastIndexOf('-'))) {
          if (p[relationName]) {
            p[relationName].push(value);
          } else {
            p[relationName] = [];
            p[relationName].push(value);
          }
          break;
        }
      }
    }
  }

  return sameClass[0] ? sameClass[0] : [];
}

export function getParentKeyByTree(key, tree) {
  let parentKey;
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node.children) {
      if (node.children.some(item => item.key === key)) {
        parentKey = node.key
      } else if (getParentKeyByTree(key, node.children)) {
        parentKey = getParentKeyByTree(key, node.children)
      }
    }
  }
  return parentKey
}

/**
 * childrenFn > childrenKey
 *
 * fn: (node, ctx, pNode) => void;
 *   ctx: {
 *     index: 1,
 *     level: 0,
 *   }
 *
 * walkTree([], (node, ctx, pNode) => {
 * });
 */
export function walkTree(
  root,
  fn, {
    childrenFn = null,
    childrenKey = 'children',
    childFirst = false,
    rootAsChildren = false,
    skipRoot = false,
    ctx = {},
    level = 0,
  } = {}
) {
  if (childrenFn === null) {
    childrenFn = node => node[childrenKey];
  }

  let processNode = (node, pNode, level, index) => {
    if (!childFirst) {
      ctx.level = level;
      ctx.index = index;
      fn(node, ctx, pNode);
    }

    processChildren(node, level);

    if (childFirst) {
      ctx.level = level;
      ctx.index = index;
      fn(node, ctx, pNode);
    }
  };
  let processChildren = (pNode, level) => {
    let children = childrenFn(pNode);
    if (children && children.length) {
      children.forEach((node, index) => processNode(node, pNode, level + 1, index));
    }
  };

  if (rootAsChildren) {
    if (root && root.length) {
      root.forEach((node, index) => processNode(node, null, level, index));
    }
  } else {
    if (skipRoot) {
      processChildren(root, level);
    } else {
      processNode(root, null, level, -1);
    }
  }
}

export function downloadFile(url, fileName) {
  const eleLink = document.createElement('a');
  eleLink.download = fileName;
  eleLink.style.display = 'none';
  eleLink.href = url;
  eleLink.target = fileName && fileName.toLowerCase().includes('.pdf') ? '_blank' : '_self';
  document.body.appendChild(eleLink);
  eleLink.click();
  document.body.removeChild(eleLink);
}


// 为数据生成唯一的uuid rowkey
export function addUUID(arr, that) {
  arr.forEach(item => {
    item.rowKey = that.UUID();
    if (item.children && item.children.length) {
      addUUID(item.children, that);
    }
  });
}

// get流文件下载
export function getDownload(res) {
  let headers = res.headers;
  let contentType = headers['content-type'];

  if (!res.data) {
    return false;
  } else {
    const blob = new Blob([res.data], {
      type: contentType
    });

    const contentDisposition = res.headers['content-disposition'];
    let fileName = 'unknown';
    if (contentDisposition) {
      fileName = window.decodeURI(res.headers['content-disposition'].split('=')[1]);
    }
    console.log('download', fileName);
    return;
    downFile(blob, fileName);
  }

  /* 下载方法 */
  function downFile(blob, fileName) {
    // 非IE下载
    if ('download' in document.createElement('a')) {
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob); // 创建下载的链接
      link.download = fileName; // 下载后文件名
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click(); // 点击下载
      window.URL.revokeObjectURL(link.href); // 释放掉blob对象
      document.body.removeChild(link); // 下载完成移除元素
    } else {
      // IE10+下载
      window.navigator.msSaveBlob(blob, fileName);
    }
  }
}

export function tileParams(params, arr) {
  let paramsArr = arr;
  Object.keys(params).forEach(key => {
    if (_.isArray(params[key])) {
      params[key].forEach(item => {
        tileParams(item, paramsArr);
      });
    } else if (_.isObject(params[key]) && !_.isArray(params[key])) {
      tileParams(params[key], paramsArr);
    } else {
      let param = {};
      param[key] = params[key];
      paramsArr.push(param);
    }
  });
}


export function initMonthCalendar(selectedDay, maxDate) {
  const firstDay = moment(selectedDay).date(1);
  let weekday = new Date(firstDay).getDay(); // 获取1日为星期几
  if (weekday === '0' || weekday === 0) { // 为0即周日转成数字7，方便计算
    weekday = 7;
  }
  const monthDays = moment(selectedDay).daysInMonth(); // 获取当前选择月天数
  const lastMonthDays = moment(selectedDay).add(-1, 'month').daysInMonth(); // 获取当前选择月天数
  // const theDay = moment(selectedDay).date(); // 当前日
  const currentYear = moment().year(); // 当前年
  const currentMonth = moment().month(); // 当前月
  let sameMonthYear = false; // 用于判断当前时间和选择的时间年月份是否一样的标识，此标识用来查找当前天
  if (currentYear === moment(selectedDay).year() && currentMonth === moment(selectedDay).month()) {
    sameMonthYear = true;
  }
  const len = monthDays + weekday - 1;
  const calendarDays = [];
  for (let i = 1; i <= 42; i += 1) {
    if (i < weekday) { // 补充当月1日前的几天凑满一周用与占位
      const formatFullDay = moment(selectedDay).add(-1, 'month').date(lastMonthDays - (weekday - i - 1)).format('YYYY-MM-DD');
      calendarDays.push({
        day: lastMonthDays - (weekday - i - 1),
        isLastMonth: true,
        atDisabledRange: moment(formatFullDay).diff(maxDate) > 0,
        formatFullDay: formatFullDay,
      });
    } else if (i >= weekday && i <= (weekday + monthDays - 1)) {
      const day = (i - weekday + 1) < 10 ? '0' + (i - weekday + 1) : (i - weekday + 1);
      const formatFullDay = moment(selectedDay).date(day).format('YYYY-MM-DD');
      const dayInfo = {
        day: i - weekday + 1,
        formatFullDay: formatFullDay,
        atDisabledRange: moment(formatFullDay).diff(maxDate) > 0,
      };
      if (i - weekday + 1 === moment().date() && sameMonthYear) {
        dayInfo.isCurrentDay = true;
      }
      // if (i - weekday + 1 === theDay) {
      //   dayInfo.isSelectedDay = true;
      // }
      calendarDays.push(dayInfo);
    } else {
      const formatFullDay = moment(selectedDay).add(1, 'month').date(-weekday - monthDays + i + 1).format('YYYY-MM-DD');
      calendarDays.push({
        day: -weekday - monthDays + i + 1,
        isNextMonth: true,
        formatFullDay: formatFullDay,
        atDisabledRange: moment(formatFullDay).diff(maxDate) > 0,
      });
    }
  }
  const calendarDayGroups = []; // 将整个月的日期分成二维数组
  for (let i = 1; i <= Math.ceil(42 / 7); i += 1) {
    const sevenDaysArray = calendarDays.slice((i - 1) * 7, (i - 1) * 7 + 7);
    calendarDayGroups.push(sevenDaysArray);
  }
  return calendarDayGroups;
}

export function checkIsImage(filepath) {
  const extStart = filepath.lastIndexOf('.');
  const ext = filepath.substring(extStart, filepath.length).toUpperCase();
  let isImage = false;
  if (
    ext != '.BMP' &&
    ext != '.PNG' &&
    ext != '.GIF' &&
    ext != '.JPG' &&
    ext != '.JPEG'
  ) {
    isImage = false;
  } else {
    isImage = true;
  }
  return isImage;
}

export function adaptiveTableScrollY(vm, minusHeight) {
  vm.scrollY = document.documentElement.clientHeight - minusHeight;
  console.log(vm.scrollY)
  window.onresize = function () {
    vm.scrollY = document.documentElement.clientHeight - minusHeight;
    const dom1 = document.getElementsByClassName('ant-table-body');
    const dom2 = document.getElementsByClassName('ant-table-body-inner');
    if (dom1) {
      for (let i = 0; i < dom1.length; i++) {
        dom1[i].style.maxHeight = vm.scrollY + 'px';
      }
    }
    if (dom2) {
      for (let i = 0; i < dom2.length; i++) {
        dom2[i].style.maxHeight = vm.scrollY + 'px';
      }
    }
  };
}

export function moneyToNumValue(val) {
  if (typeof val === 'number') {
    return val;
  } else {
    var num = val.trim();
    var ss = num.toString();
    if (ss.length == 0) {
      return '0';
    }
    return ss.replace(/,/g, '');
  }
}